class PDPAccordion extends HTMLElement {
  constructor() {
    super();
    this._isInitialized = false;
  }

  static get observedAttributes() {
    return [
      'heading',
      'icon',
      'background-color',
      'foreground-color',
      'border-color',
      'border-thickness',
      'border-radius',
      'padding-top',
      'padding-bottom'
    ];
  }

  connectedCallback() {
    if (!this._isInitialized) {
      this.render();
      this.setupEventListeners();
      this._isInitialized = true;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this._isInitialized) {
      this.updateStyles();
    }
  }

  get heading() {
    return this.getAttribute('heading') || 'Accordion Heading';
  }

  get icon() {
    return this.getAttribute('icon') || 'plus';
  }

  get backgroundColor() {
    return this.getAttribute('background-color') || '';
  }

  get foregroundColor() {
    return this.getAttribute('foreground-color') || '';
  }

  get borderColor() {
    return this.getAttribute('border-color') || '';
  }

  get borderThickness() {
    return this.getAttribute('border-thickness') || '';
  }

  get borderRadius() {
    return this.getAttribute('border-radius') || '';
  }

  get paddingTop() {
    return this.getAttribute('padding-top') || '0';
  }

  get paddingBottom() {
    return this.getAttribute('padding-bottom') || '0';
  }

  render() {
    // Store original content
    const originalContent = this.innerHTML;

    const iconSVG = this.icon === 'caret'
      ? `<svg class="pdp-accordion-icon icon-caret" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z"></path>
        </svg>`
      : `<svg class="pdp-accordion-icon icon-plus" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line class="horizontal" x1="4" y1="12" x2="20" y2="12" />
          <line class="vertical" x1="12" y1="4" x2="12" y2="20" />
        </svg>`;

    this.innerHTML = `
      <style>
        pdp-accordion {
          display: block;
          padding-top: ${this.paddingTop}px;
          padding-bottom: ${this.paddingBottom}px;
        }

        pdp-accordion .pdp-accordion {
          width: 100%;
          max-width: 100%;
          position: relative;
          box-sizing: border-box;
        }

        pdp-accordion .pdp-accordion-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          cursor: pointer;
          transition: background-color 0.2s ease;
          user-select: none;
          box-sizing: border-box;
        }

        pdp-accordion .pdp-accordion-header-content {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex: 1;
        }

        pdp-accordion .pdp-accordion-header-content p {
          font-size: 0.875rem;
          line-height: 1.25rem;
          margin: 0;
          font-weight: 500;
        }

        pdp-accordion .pdp-accordion-content {
          font-size: 0.875rem;
          line-height: 1.25rem;
          max-width: 100%;
          overflow: hidden;
          transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          max-height: 0;
          box-sizing: border-box;
        }

        pdp-accordion .pdp-accordion-content p {
          display: block;
          margin: 1em 0;
        }

        pdp-accordion .pdp-accordion-content h1,
        pdp-accordion .pdp-accordion-content h2,
        pdp-accordion .pdp-accordion-content h3,
        pdp-accordion .pdp-accordion-content h4,
        pdp-accordion .pdp-accordion-content h5,
        pdp-accordion .pdp-accordion-content h6 {
          display: block;
          font-weight: 500;
        }

        pdp-accordion .pdp-accordion-content h1 {
          font-size: 2em;
          margin: 0.67em 0;
        }

        pdp-accordion .pdp-accordion-content h2 {
          font-size: 1.5em;
          margin: 0.83em 0;
        }

        pdp-accordion .pdp-accordion-content h3 {
          font-size: 1.17em;
          margin: 1em 0;
        }

        pdp-accordion .pdp-accordion-content h4 {
          font-size: 1em;
          margin: 1.33em 0;
        }

        pdp-accordion .pdp-accordion-content h5 {
          font-size: 0.83em;
          margin: 1.67em 0;
        }

        pdp-accordion .pdp-accordion-content h6 {
          font-size: 0.67em;
          margin: 2.33em 0;
        }

        pdp-accordion .pdp-accordion-content img {
          height: auto;
          max-width: 100%;
        }

        pdp-accordion .pdp-accordion-content ul,
        pdp-accordion .pdp-accordion-content ol {
          list-style: disc;
          list-style-position: inside;
          padding-left: 1rem !important;
        }

        pdp-accordion .pdp-accordion-content li {
          list-style: inherit;
        }

        pdp-accordion .pdp-accordion-content li:last-child {
          margin-bottom: 0;
        }

        pdp-accordion .pdp-accordion-content a {
          color: var(--color-article-link-foreground);
          text-decoration: underline;
          text-underline-offset: 0.3rem;
          text-decoration-thickness: 0.1rem;
          transition: text-decoration-thickness 150ms ease;
        }

        pdp-accordion .pdp-accordion-content a:hover {
          text-decoration-thickness: 0.2rem;
        }

        pdp-accordion .pdp-accordion-content blockquote {
          display: inline-flex;
        }

        pdp-accordion .pdp-accordion-content blockquote > * {
          margin: 0.5rem 0;
        }

        pdp-accordion .pdp-accordion-inner {
          padding-inline: 1rem;
          padding-bottom: 0.75rem;
          box-sizing: border-box;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }

        pdp-accordion .pdp-accordion-icon {
          width: 1.25rem;
          height: 1.25rem;
          flex-shrink: 0;
        }

        pdp-accordion .pdp-accordion-icon.icon-caret {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform-origin: center;
        }

        pdp-accordion .pdp-accordion-icon.icon-caret.rotate {
          transform: rotate(180deg);
        }

        pdp-accordion .pdp-accordion-icon.icon-plus line {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform-origin: 12px 12px;
        }

        pdp-accordion .pdp-accordion-icon.icon-plus.rotate .vertical {
          opacity: 0;
          transform: rotate(90deg);
        }

        pdp-accordion .pdp-accordion-icon.icon-plus.rotate .horizontal {
          transform: rotate(0deg);
        }
      </style>

      <div class="pdp-accordion">
        <div class="pdp-accordion-header">
          <div class="pdp-accordion-header-content">
            <p>${this.heading}</p>
          </div>
          ${iconSVG}
        </div>

        <div class="pdp-accordion-content">
          <div class="pdp-accordion-inner">
            ${originalContent}
          </div>
        </div>
      </div>
    `;

    this.updateStyles();
  }

  updateStyles() {
    const accordion = this.querySelector('.pdp-accordion');
    if (!accordion) return;

    // Apply custom styles if provided, otherwise rely on global styles
    if (this.backgroundColor) {
      accordion.style.backgroundColor = this.backgroundColor;
    }
    if (this.foregroundColor) {
      accordion.style.color = this.foregroundColor;
    }
    if (this.borderColor) {
      accordion.style.borderBottomColor = this.borderColor;
    }
    if (this.borderThickness) {
      accordion.style.borderBottomWidth = this.borderThickness + 'px';
      accordion.style.borderBottomStyle = 'solid';
    }
    if (this.borderRadius) {
      accordion.style.borderRadius = this.borderRadius + 'px';
    }
  }

  setupEventListeners() {
    const header = this.querySelector('.pdp-accordion-header');
    const content = this.querySelector('.pdp-accordion-content');
    const inner = this.querySelector('.pdp-accordion-inner');
    const icon = this.querySelector('.pdp-accordion-icon');

    if (!header || !content || !inner) return;

    header.addEventListener('click', () => {
      if (content.classList.contains('open')) {
        content.style.maxHeight = '0px';
        content.classList.remove('open');
        if (icon) icon.classList.remove('rotate');
        this.dispatchEvent(new CustomEvent('accordion-close', { bubbles: true }));
      } else {
        content.classList.add('open');
        const height = inner.scrollHeight;
        content.style.maxHeight = height + 'px';
        if (icon) icon.classList.add('rotate');
        this.dispatchEvent(new CustomEvent('accordion-open', { bubbles: true }));
        
        // Recalculate height after rendering
        setTimeout(() => {
          if (content.classList.contains('open')) {
            content.style.maxHeight = inner.scrollHeight + 'px';
          }
        }, 50);
      }
    });
  }

  // Public API
  open() {
    const content = this.querySelector('.pdp-accordion-content');
    const inner = this.querySelector('.pdp-accordion-inner');
    const icon = this.querySelector('.pdp-accordion-icon');

    if (content && inner && !content.classList.contains('open')) {
      content.classList.add('open');
      content.style.maxHeight = inner.scrollHeight + 'px';
      if (icon) icon.classList.add('rotate');
      this.dispatchEvent(new CustomEvent('accordion-open', { bubbles: true }));
    }
  }

  close() {
    const content = this.querySelector('.pdp-accordion-content');
    const icon = this.querySelector('.pdp-accordion-icon');

    if (content && content.classList.contains('open')) {
      content.style.maxHeight = '0px';
      content.classList.remove('open');
      if (icon) icon.classList.remove('rotate');
      this.dispatchEvent(new CustomEvent('accordion-close', { bubbles: true }));
    }
  }

  toggle() {
    const content = this.querySelector('.pdp-accordion-content');
    if (content && content.classList.contains('open')) {
      this.close();
    } else {
      this.open();
    }
  }

  isOpen() {
    const content = this.querySelector('.pdp-accordion-content');
    return content ? content.classList.contains('open') : false;
  }
}

// Register the custom element
customElements.define('pdp-accordion', PDPAccordion);